"use client";

import Link from "next/link";
import { NavItem } from "../types";
import { isItemActive } from "../utils";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";

interface MobileNavItemProps {
	item: NavItem;
	currentPath: string;
	onNavigate?: () => void;
}

const MobileLink = ({ item, currentPath, onNavigate }: MobileNavItemProps) => {
	const active = item.path === currentPath;
	return (
		<Link
			href={item.active ? item.path : "#"}
			onClick={item.active ? onNavigate : undefined}
			className={cn(
				"flex items-center gap-3 rounded-sm px-3 py-2 text-sm font-body tracking-wide transition-colors",
				active
					? "bg-muted text-terracotta"
					: "text-ink hover:bg-muted hover:text-terracotta",
				!item.active && "cursor-not-allowed pointer-events-none opacity-50"
			)}
			aria-label={`Navegar a ${item.label}`}
		>
			{item.iconId && (
				<span className="text-base">
					<Icon iconId={item.iconId} color="yellow" />
				</span>
			)}
			<span>{item.label}</span>
		</Link>
	);
};

export const MobileNavItem = ({
	item,
	currentPath,
	onNavigate,
}: MobileNavItemProps) => {
	if (!item.visible) return null;

	if (!item.children?.length) {
		return (
			<MobileLink item={item} currentPath={currentPath} onNavigate={onNavigate} />
		);
	}

	const active = isItemActive(item, currentPath);

	return (
		<AccordionItem value={item.path} className="border-b-0">
			<AccordionTrigger
				className={cn(
					"rounded-sm px-3 py-2 text-sm font-body tracking-wide hover:no-underline",
					active ? "text-terracotta" : "text-ink"
				)}
			>
				{item.label}
			</AccordionTrigger>
			<AccordionContent className="pb-1">
				<Accordion
					type="multiple"
					className="ml-3 flex flex-col gap-1 border-l border-border pl-2"
				>
					{item.children.map((child) => (
						<MobileNavItem
							key={child.path}
							item={child}
							currentPath={currentPath}
							onNavigate={onNavigate}
						/>
					))}
				</Accordion>
			</AccordionContent>
		</AccordionItem>
	);
};
